import { Component, input, computed, inject } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router, ActivatedRoute } from '@angular/router';
import {
  OperativeResponse,
  OperativaGroup,
  OperativaGroupWithAssignment,
  BaseOperativeEntity,
} from 'src/app/shared/interfaces/holidays.interface';

interface BottomCardItem {
  id: string;
  title: string;
  value: number;
  value2?: number;
  status: 'success' | 'warning' | 'error';
}

// Type alias for the operative input - union type for both OperativeResponse and OperativaGroup
export type OperativeOrGroup =
  | OperativeResponse
  | OperativaGroup
  | OperativaGroupWithAssignment;

@Component({
  selector: 'app-operational-card',
  standalone: true,
  imports: [MatIconModule, MatSlideToggleModule, MatDividerModule],
  templateUrl: './operational-card.component.html',
  styleUrls: ['./operational-card.component.css', '../../../styles/layout.css'],
})
export class OperationalCardComponent {
  // Input signal with proper union type - accepts either OperativeResponse or OperativaGroup
  readonly operative = input<OperativeOrGroup | null | undefined>();

  // Type guard to check if the operative is an OperativaGroup
  // OperativaGroup has total_drivers/total_trucks while OperativeResponse has stats
  private isOperativaGroup(
    op: OperativeOrGroup | null | undefined
  ): op is OperativaGroup {
    return (
      op !== null &&
      op !== undefined &&
      'total_drivers' in op &&
      'total_trucks' in op
    );
  }

  // computed signal that returns an array suitable for @for
  readonly bottomItems = computed<BottomCardItem[]>(() => {
    const op = this.operative();
    if (!op) return [];

    // Handle OperativaGroup differently from OperativeResponse
    if (this.isOperativaGroup(op)) {
      return [
        {
          id: 'conductores',
          title: 'CHÓFERES',
          value: op.total_drivers || 0,
          status: 'success',
        },
        {
          id: 'camiones',
          title: 'CAMIONES',
          value: op.total_trucks || 0,
          status: 'success',
        },
      ];
    }

    // Handle OperativeResponse (legacy)
    const stats = op.stats;
    return [
      // {
      //   id: 'camiones',
      //   title: 'CAMIONES',
      //   value: stats?.camiones.actual || 0,
      //   value2: stats?.camiones.total || 0,
      //   status: stats?.camiones.status || 'success',
      // },
      {
        id: 'conductores',
        title: 'CONDUCTORES',
        value: stats?.conductores.actual || 0,
        value2: stats?.conductores.total || 0,
        status: stats?.conductores.status || 'success',
      },
      {
        id: 'notificaciones',
        title: 'NOTIFICACIONES',
        value: stats?.notificaciones_count ?? 0,
        status: (stats?.notificaciones_count ?? 0) > 0 ? 'error' : 'success',
      },
    ];
  });

  // computed signal for petition count
  readonly petitionCount = computed(() => {
    const op = this.operative();
    if (!op) return 0;

    // Check if it's an OperativaGroupWithAssignment with petitions field
    if (this.isOperativaGroup(op) && 'petitions' in op) {
      return (op as OperativaGroupWithAssignment).petitions ?? 0;
    }

    // Check if it's an OperativeResponse with pending_petitions
    if (!this.isOperativaGroup(op)) {
      return (op as OperativeResponse).pending_petitions?.count ?? 0;
    }

    return 0;
  });

  // computed signal for petition message
  readonly petitionMessage = computed(() => {
    const op = this.operative();
    if (!op) return 'No hay peticiones';

    const count = this.petitionCount();

    // For OperativaGroup, generate a simple message (without the count, since it's shown separately)
    if (this.isOperativaGroup(op)) {
      return count > 0
        ? `petición${count !== 1 ? 'es' : ''} pendiente${
            count !== 1 ? 's' : ''
          }`
        : 'No hay peticiones';
    }

    // For OperativeResponse, use the existing message
    return (
      (op as OperativeResponse).pending_petitions?.message ??
      'No hay peticiones'
    );
  });

  private route = inject(ActivatedRoute);

  constructor(private router: Router) {}

  goToOperational() {
    const op = this.operative();
    const exerciseId = this.route.snapshot.params['id'];
    const year = this.route.snapshot.params['year'];
    if (!op?.id || !exerciseId) return;

    this.router.navigate([
      '/home',
      'vacations',
      'drivers',
      'exercise',
      exerciseId,
      year,
      'operational',
      op.id,
    ]);
  }

  trackById(_index: number, item: BottomCardItem): string {
    return item.id;
  }
}
